// No tenant chosen yet. Staff pick a customer to open as them, or go to the staff screens; a person who
// belongs to several workspaces picks the one to work in. The choice is kept for the browser session.
import { useNavigate } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { useAuth } from "../auth/AuthProvider";
import { AuthFrame, AuthHeading, TextButton } from "../shared/AuthFrame";
import { Badge, Button, Icon } from "../ui";
import { roleWord, tierName } from "../shared/format";
import type { Role, TenantState } from "../api/types";

interface Row { id: string; name: string; state: TenantState; tier: string; dryRun: boolean; role?: Role }

export function TenantPicker() {
  const session = useSession();
  const auth = useAuth();
  const navigate = useNavigate();
  const email = auth.account?.email ?? session.me.user.email ?? session.me.user.name;
  // An offboarded customer can't be opened any more; the staff console still lists it.
  const rows: Row[] = session.isStaff ? (session.me.staffTenants ?? []).filter((t) => t.state !== "Offboarded") : session.workspaces;
  return (
    <AuthFrame width={460} footer={<>Signed in as {email}. <TextButton onClick={auth.signOut} style={{ fontSize: 14 }}>Sign out</TextButton></>}>
      {session.isStaff ? (
        <>
          <AuthHeading title="Open as customer" text="AutoVouch staff: every action is logged under your name." />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button leadingIcon={<Icon name="shield" size={16} />} onClick={() => navigate("/staff")}>Staff console</Button>
            <Button variant="outline" leadingIcon={<Icon name="radar" size={16} />} onClick={() => navigate("/ops")}>Mission control</Button>
          </div>
        </>
      ) : (
        <AuthHeading title="Pick a workspace" text={`You’re in ${rows.length}. Switch any time from the menu.`} />
      )}
      {rows.length === 0 ? (
        <p style={{ margin: 0, font: "var(--type-body)", color: "var(--text-muted)" }}>{session.isStaff ? "No customers yet. Create the first one in the staff console." : "You’re not in a workspace yet. Open the invitation link you were sent."}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "50vh", overflowY: "auto" }}>
          {rows.map((t) => (
            <button key={t.id} type="button" className="avp-mi" onClick={() => { session.openTenant(t.id); navigate("/"); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-lg)", background: "var(--surface-card)", cursor: "pointer", textAlign: "left" }}>
              <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--gradient-fresh)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", font: "800 14px var(--font-display)", flex: "none" }}>{t.name.slice(0, 2).toUpperCase()}</span>
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ font: "700 16px var(--font-body)", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                <span style={{ font: "13px var(--font-body)", color: "var(--text-muted)" }}>{t.role ? roleWord(t.role) : `${t.id} – ${tierName(t.tier)}`}</span>
              </span>
              {t.state === "Paused" ? <Badge tone="neutral">Paused</Badge> : t.dryRun ? <Badge tone="pending">Preview mode</Badge> : <Badge tone="matched">Live</Badge>}
              <Icon name="chevron-right" size={18} style={{ color: "var(--text-brand)" }} />
            </button>
          ))}
        </div>
      )}
    </AuthFrame>
  );
}
